import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Alert,
    Avatar,
    Box,
    Breadcrumbs,
    Chip,
    CircularProgress,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import {indigo, teal} from "@mui/material/colors";
import {
    AddOutlined,
    CodeOutlined,
    DeleteOutline,
    FactCheckOutlined,
    FileDownloadOutlined,
    ListAltOutlined,
    PlayArrowOutlined,
    PlaylistAddCheckOutlined,
    SaveOutlined,
    StarBorderOutlined,
    StopOutlined,
    UploadFileOutlined,
    VisibilityOutlined
} from "@mui/icons-material";
import {NavLink, Redirect} from "react-router-dom";
import {Page} from "../app/Page";
import AppConsts from "../app/AppConsts";
import {PathsMap} from "../app/PageRouting";
import {HeadPaper} from "../app/Theme";
import {useUser} from "../user-auth/UserProvider";
import {UserRequiredRedirect} from "../user-auth/UserRequiredRedirect";
import {useSnackbar} from "../providers/SnackbarProvider";
import {logError} from "../utils/logging";
import {fetchPrograms, MAX_CASES_PER_REQUEST} from "./dlv/dlvApi";
import {useDlvTestRun} from "./dlv/useDlvTestRun";
import {DlvTestResults} from "./dlv/DlvTestResults";
import {
    DeleteProgramDialog,
    ImportTestsDialog,
    SetDefaultProgramDialog,
    UploadProgramDialog,
    ViewProgramDialog
} from "./dlv/DlvProgramDialogs";
import {DlvYamlEditor} from "./dlv/DlvYamlEditor";
import {parseDlvTests} from "./dlv/parseDlvTests";
import {appendNewTest} from "./dlv/newTest";
import {caseFieldId, DlvTestsForm} from "./dlv/DlvTestsForm";
import {useFormCases} from "./dlv/useFormCases";
import {useProgramTests} from "./dlv/useProgramTests";
import {DlvTestsHelp} from "./dlv/DlvTestsHelp";

const MODE_STORAGE_KEY = 'dlvTestsMode';
const CONTROL_HEIGHT = 40;
const BUTTON_SX = {textTransform: 'none', height: CONTROL_HEIGHT};

export function DlvAdminPageMiddleware() {
    const user = useUser();
    if (!user.logged) {
        return <UserRequiredRedirect/>;
    }
    if (!user.data.canManageDlvPrograms) {
        return <Redirect to={PathsMap.HOME}/>;
    }
    return <DlvAdminPage/>;
}

function loadSavedMode() {
    try {
        return window.localStorage.getItem(MODE_STORAGE_KEY) === 'yaml' ? 'yaml' : 'form';
    } catch (e) {
        return 'form';
    }
}

function saveMode(value) {
    try {
        window.localStorage.setItem(MODE_STORAGE_KEY, value);
    } catch (e) {
        // The mode just goes back to the form on the next visit
    }
}

function PanelHead({icon, color, title}) {
    return (
        <HeadPaper sx={{p: 3}} color={color}>
            <Stack direction="row" alignItems="center" spacing={3}>
                <Avatar sx={{border: '1px solid ' + color, bgcolor: 'transparent'}}>{icon}</Avatar>
                <Typography variant="h2" sx={{mb: 3, mt: 3}}>{title}</Typography>
            </Stack>
        </HeadPaper>
    );
}

function DlvAdminPage() {
    const {createSnackbar} = useSnackbar();
    const run = useDlvTestRun();

    const [programs, setPrograms] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [selected, setSelected] = useState('');
    const tests = useProgramTests(selected);
    const {input, setInput} = tests;
    const [mode, setMode] = useState(loadSavedMode);
    const [dialog, setDialog] = useState(null);

    const {cases, error: inputError} = useMemo(() => parseDlvTests(input), [input]);
    const form = useFormCases(input, setInput);
    const running = run.state.status === 'running';

    const applyPrograms = useCallback((response, preferredSelection) => {
        setPrograms(response);
        setSelected(current => {
            const names = response.programs.map(p => p.name);
            if (preferredSelection && names.includes(preferredSelection)) {
                return preferredSelection;
            }
            if (names.includes(current)) {
                return current;
            }
            return names.includes(response.defaultProgram) ? response.defaultProgram : (names[0] ?? '');
        });
    }, []);

    useEffect(() => {
        let mounted = true;
        fetchPrograms()
            .then(response => mounted && applyPrograms(response))
            .catch(e => {
                logError(e);
                if (mounted) {
                    setLoadError(e.message);
                }
            });
        return () => {
            mounted = false;
        };
    }, [applyPrograms]);

    useEffect(() => saveMode(mode), [mode]);

    // The results belong to the program they were run on
    const resetRun = run.reset;
    useEffect(() => resetRun(), [selected, resetRun]);

    useEffect(() => {
        if (!tests.dirty) {
            return undefined;
        }
        const warn = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [tests.dirty]);

    const defaultProgram = programs?.defaultProgram ?? '';
    const defaultExists = programs?.programs.some(p => p.name === defaultProgram) ?? false;
    const selectedIsDefault = selected !== '' && selected === defaultProgram;

    const selectedProgram = programs?.programs.find(p => p.name === selected);
    const testsReady = tests.status === 'ready';

    const handleSelect = (name) => {
        if (name === selected) {
            return;
        }
        if (tests.dirty && !window.confirm(`Hai modifiche non salvate ai test di ${selected}. Vuoi scartarle?`)) {
            return;
        }
        setSelected(name);
    };

    const saveTests = async () => {
        const name = selected;
        try {
            await tests.save(input);
            setPrograms(current => current && {
                ...current,
                programs: current.programs.map(p => p.name === name ? {...p, hasTests: input.trim() !== ''} : p)
            });
            createSnackbar({message: 'Test salvati', type: 'success'});
        } catch (e) {
            logError(e);
            createSnackbar({message: e.message, type: 'error'});
        }
    };

    const changeMode = (next) => {
        if (next === 'form' && form.error) {
            createSnackbar({message: 'Correggi il YAML prima di passare al modulo', type: 'error'});
            return;
        }
        setMode(next);
    };

    const addTest = () => {
        if (mode === 'form') {
            if (form.error) {
                createSnackbar({message: 'Correggi il YAML prima di aggiungere un test', type: 'error'});
                return;
            }
            const key = form.add();
            setTimeout(() => document.getElementById(caseFieldId(key, 'description'))?.focus(), 0);
            return;
        }

        const added = appendNewTest(input);
        if (!added) {
            createSnackbar({message: 'Correggi il YAML prima di aggiungere un test', type: 'error'});
            return;
        }
        setInput(added.text);
        // Wait for the editor to show the new text, then put the cursor on the description of the new test
        setTimeout(() => {
            const textarea = document.getElementById('dlv-tests-editor');
            if (!textarea) {
                return;
            }
            textarea.focus();
            textarea.setSelectionRange(added.caret, added.caret);
            const scroller = textarea.closest('[data-editor-scroll]');
            if (scroller) {
                scroller.scrollTop = scroller.scrollHeight;
            }
        }, 0);
    };

    const runTests = () => {
        if (cases && selected && !running) {
            run.start(cases, selected);
        }
    };

    const closeDialog = () => setDialog(null);

    const allCasesOpen = !form.error && form.cases.every(c => c.open);

    const blocks = cases ? Math.ceil(cases.length / MAX_CASES_PER_REQUEST) : 0;
    const inputHelp = inputError
        ?? (cases
            ? `${cases.length} test${blocks > 1 ? `, eseguiti in ${blocks} blocchi da ${MAX_CASES_PER_REQUEST}` : ''}`
            : 'Un elenco di casi con description, facts ed expected.');
    const keyboardHint = "Tab indenta; premi Esc e poi Tab per uscire dall'editor.";

    return (
        <Page title="Programma logico" maxWidth="xl">
            <Stack spacing={3} sx={{mt: 3}}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" to={PathsMap.HOME} component={NavLink}>
                        {AppConsts.NAME}
                    </Link>
                    <Typography color="text.primary">Programma logico</Typography>
                </Breadcrumbs>

                <Paper variant="outlined" sx={{p: 3}}>
                    <Stack spacing={2.5}>
                        <Box>
                            <Typography variant="h2">Programma logico</Typography>
                            <Typography color="text.secondary" sx={{mt: 1}}>
                                Prova un programma con i tuoi test, caricane di nuovi e scegli quello che genera i
                                risultati dei triage.
                            </Typography>
                        </Box>

                        {loadError && <Alert severity="error">{loadError}</Alert>}
                        {programs && !defaultExists &&
                            <Alert severity="warning">
                                Il programma predefinito ({defaultProgram}) non esiste ancora: finché non ne imposti
                                uno, i triage non riescono a generare i risultati.
                            </Alert>}

                        <Stack direction={{xs: 'column', md: 'row'}} spacing={2} alignItems={{xs: 'stretch', md: 'center'}}>
                            <FormControl size="small" disabled={!programs}
                                         sx={{minWidth: {md: 340}, flexGrow: {md: 0}, '& .MuiInputBase-root': {height: CONTROL_HEIGHT}}}>
                                <InputLabel id="dlv-program-label">Programma</InputLabel>
                                <Select
                                    labelId="dlv-program-label"
                                    label="Programma"
                                    value={selected}
                                    onChange={(e) => handleSelect(e.target.value)}
                                    renderValue={(value) => (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <span>{value}</span>
                                            {value === defaultProgram &&
                                                <Chip size="small" color="primary" variant="outlined"
                                                      label="Predefinito"/>}
                                        </Stack>
                                    )}>
                                    {programs?.programs.map(program =>
                                        <MenuItem key={program.name} value={program.name}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <span>{program.name}</span>
                                                {program.isDefault &&
                                                    <Chip size="small" color="primary" variant="outlined"
                                                          label="Predefinito"/>}
                                            </Stack>
                                        </MenuItem>)}
                                </Select>
                            </FormControl>
                            {!programs && !loadError && <CircularProgress size={20}/>}
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                <Button variant="outlined" startIcon={<VisibilityOutlined/>}
                                        disabled={!selected} onClick={() => setDialog('view')} sx={BUTTON_SX}>
                                    Visualizza
                                </Button>
                                <Button variant="outlined" startIcon={<StarBorderOutlined/>}
                                        disabled={!selected || selectedIsDefault}
                                        onClick={() => setDialog('default')} sx={BUTTON_SX}>
                                    Imposta come predefinito
                                </Button>
                                <Button variant="outlined" color="error" startIcon={<DeleteOutline/>}
                                        disabled={!selected || selectedIsDefault}
                                        onClick={() => setDialog('delete')} sx={BUTTON_SX}>
                                    Elimina
                                </Button>
                            </Stack>
                            <Box sx={{flexGrow: 1, display: {xs: 'none', md: 'block'}}}/>
                            <Button variant="outlined" startIcon={<UploadFileOutlined/>}
                                    disabled={!programs} onClick={() => setDialog('upload')} sx={BUTTON_SX}>
                                Carica programma
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>

                {/* A Stack with spacing overrides the negative margin of a Grid container child: wrap it */}
                <Box>
                <Grid container spacing={3} alignItems="flex-start">
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined">
                            <PanelHead icon={<FactCheckOutlined sx={{fontSize: 26, color: indigo[500]}}/>}
                                       color={indigo[300]} title="Test"/>
                            <Stack spacing={2} sx={{p: 3}}>
                                {tests.status === 'loading' &&
                                    <Stack alignItems="center" sx={{py: 8}}>
                                        <CircularProgress aria-label="Caricamento dei test"/>
                                    </Stack>}
                                {tests.status === 'error' &&
                                    <Alert severity="error"
                                           action={<Button color="inherit" size="small" onClick={tests.retry}
                                                           sx={{textTransform: 'none'}}>Riprova</Button>}>
                                        {tests.error}
                                    </Alert>}
                                {tests.status === 'idle' &&
                                    <Typography color="text.secondary">
                                        Scegli un programma per vedere e modificare i suoi test.
                                    </Typography>}
                                {testsReady && <>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                    <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={mode}
                                        onChange={(e, value) => value && changeMode(value)}
                                        aria-label="Come modificare i test">
                                        <ToggleButton value="form" sx={{textTransform: 'none', gap: 0.75}}>
                                            <ListAltOutlined fontSize="small"/> Modulo
                                        </ToggleButton>
                                        <ToggleButton value="yaml" sx={{textTransform: 'none', gap: 0.75}}>
                                            <CodeOutlined fontSize="small"/> YAML
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        {tests.dirty &&
                                            <Chip size="small" color="warning" variant="outlined"
                                                  label="Modifiche non salvate"/>}
                                        {mode === 'form' && !form.error && form.cases.length > 1 &&
                                            <Button size="small" color="inherit" sx={{textTransform: 'none'}}
                                                    onClick={() => form.setAllOpen(!allCasesOpen)}>
                                                {allCasesOpen ? 'Comprimi tutti' : 'Espandi tutti'}
                                            </Button>}
                                        <DlvTestsHelp/>
                                    </Stack>
                                </Stack>
                                {mode === 'form'
                                    ? <Box>
                                        <DlvTestsForm form={form} disabled={running} onAdd={addTest}
                                                      onShowYaml={() => setMode('yaml')}/>
                                        {!form.error && form.cases.length > 0 &&
                                            <FormHelperText error={Boolean(inputError)} sx={{mx: 1.75}}>
                                                {inputHelp}
                                            </FormHelperText>}
                                    </Box>
                                    : <Box>
                                        <DlvYamlEditor
                                            id="dlv-tests-editor"
                                            label="Casi di test in formato YAML"
                                            value={input}
                                            onChange={setInput}
                                            error={Boolean(inputError)}
                                            placeholder={'cases:\n  - description: ...\n    facts: |\n      paziente(p1).\n    expected:\n      - destinazione(p1, D)'}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                                    e.preventDefault();
                                                    runTests();
                                                }
                                            }}/>
                                        <FormHelperText error={Boolean(inputError)} sx={{mx: 1.75}}>
                                            {inputHelp}{!inputError && `. ${keyboardHint}`}
                                        </FormHelperText>
                                    </Box>}
                                </>}
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                                    <Button variant="outlined" startIcon={<AddOutlined/>} onClick={addTest}
                                            disabled={running || !testsReady} sx={BUTTON_SX}>
                                        Aggiungi test
                                    </Button>
                                    <Button variant="outlined" startIcon={<FileDownloadOutlined/>}
                                            onClick={() => setDialog('import')}
                                            disabled={running || !testsReady} sx={BUTTON_SX}>
                                        Importa test
                                    </Button>
                                    <Button variant="outlined"
                                            startIcon={tests.saving
                                                ? <CircularProgress size={16} color="inherit"/>
                                                : <SaveOutlined/>}
                                            onClick={saveTests}
                                            disabled={!tests.dirty || tests.saving} sx={BUTTON_SX}>
                                        Salva test
                                    </Button>
                                    <Box sx={{flexGrow: 1}}/>
                                    {running
                                        ? <Button variant="outlined" startIcon={<StopOutlined/>} onClick={run.stop}
                                                  sx={BUTTON_SX}>
                                            Interrompi
                                        </Button>
                                        : <Button variant="contained" startIcon={<PlayArrowOutlined/>}
                                                  disabled={!cases || !selected || !testsReady} onClick={runTests} sx={BUTTON_SX}>
                                            Esegui test
                                        </Button>}
                                </Stack>
                            </Stack>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined">
                            <PanelHead icon={<PlaylistAddCheckOutlined sx={{fontSize: 26, color: teal[500]}}/>}
                                       color={teal[300]} title="Risultati"/>
                            <DlvTestResults state={run.state} onResume={run.resume}
                                            onImport={() => setDialog('import')}/>
                        </Paper>
                    </Grid>
                </Grid>
                </Box>
            </Stack>

            <ViewProgramDialog
                open={dialog === 'view'}
                programName={selected}
                isDefault={selectedIsDefault}
                programs={programs?.programs.map(p => p.name) ?? []}
                onClose={closeDialog}
                onSaved={(response) => applyPrograms(response)}
                onDuplicated={(response, name) => {
                    // Don't move away from tests that aren't saved yet
                    applyPrograms(response, tests.dirty ? undefined : name);
                    closeDialog();
                    createSnackbar({message: `Programma duplicato come ${name}`, type: 'success'});
                }}/>
            <UploadProgramDialog
                open={dialog === 'upload'}
                onClose={closeDialog}
                programs={programs?.programs.map(p => p.name) ?? []}
                defaultProgram={defaultProgram}
                onUploaded={(response, name) => {
                    // Don't move away from tests that aren't saved yet
                    applyPrograms(response, tests.dirty ? undefined : name);
                    closeDialog();
                    createSnackbar({message: 'Programma caricato', type: 'success'});
                }}/>
            <DeleteProgramDialog
                open={dialog === 'delete'}
                programName={selected}
                hasTests={Boolean(selectedProgram?.hasTests)}
                onClose={closeDialog}
                onDeleted={(response) => {
                    applyPrograms(response);
                    closeDialog();
                    createSnackbar({message: 'Programma eliminato', type: 'success'});
                }}/>
            <SetDefaultProgramDialog
                open={dialog === 'default'}
                programName={selected}
                hasTests={Boolean(selectedProgram?.hasTests)}
                defaultProgram={defaultProgram}
                cases={cases}
                casesError={inputError}
                onClose={closeDialog}
                onChanged={(response) => {
                    applyPrograms(response);
                    closeDialog();
                    createSnackbar({message: 'Programma predefinito aggiornato', type: 'success'});
                }}/>
            <ImportTestsDialog
                open={dialog === 'import'}
                programs={programs?.programs ?? []}
                currentName={selected}
                currentInput={input}
                onClose={closeDialog}
                onImport={(text, sourceName) => {
                    setInput(text);
                    closeDialog();
                    createSnackbar({message: `Test importati da ${sourceName}: salvali per conservarli`, type: 'success'});
                }}/>
        </Page>
    );
}
